import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { MapPin, Save, RotateCcw, Compass } from "lucide-react";

// Terracotta pin icon matching the rest of the portal maps.
const pinIcon = L.divIcon({
  className: "hh-position-pin",
  html: `<div style="
    width:34px;height:34px;
    background:#C55A3C;color:#fff;
    border:3px solid #fff;border-radius:50% 50% 50% 0;
    transform:translate(-50%,-100%) rotate(-45deg);
    box-shadow:0 6px 16px -4px rgba(35,31,27,.45);
    display:flex;align-items:center;justify-content:center;
    cursor:grab;
  "><span style="transform:rotate(45deg);font-weight:900;font-size:15px;font-family:Manrope,system-ui,sans-serif">◉</span></div>`,
  iconSize: [1, 1],
  iconAnchor: [0, 0],
});

// Recenter the map on `pos` whenever the caller updates it (e.g. after geocoding).
function Recenter({ pos, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!pos) return;
    map.setView(pos, zoom ?? map.getZoom(), { animate: true });
  }, [pos, zoom, map]);
  return null;
}

export default function PositionPicker({ property, onSaved }) {
  const { lang } = useLang();
  const IT = lang === "it";

  // Italy fallback when the property has no coords yet.
  const initial = property?.lat && property?.lng ? [property.lat, property.lng] : [42.5, 12.5];
  const initialZoom = property?.lat ? 16 : 6;

  const [pos, setPos] = useState(initial);
  const [savedPos, setSavedPos] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const markerRef = useRef(null);

  useEffect(() => {
    if (property?.lat && property?.lng) {
      setPos([property.lat, property.lng]);
      setSavedPos([property.lat, property.lng]);
    }
  }, [property?.lat, property?.lng]);

  const dirty = pos[0] !== savedPos[0] || pos[1] !== savedPos[1];

  const eventHandlers = useMemo(() => ({
    dragend: () => {
      const m = markerRef.current;
      if (!m) return;
      const ll = m.getLatLng();
      setPos([Number(ll.lat.toFixed(6)), Number(ll.lng.toFixed(6))]);
    },
  }), []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/properties/me", { lat: pos[0], lng: pos[1] });
      setSavedPos(pos);
      toast.success(IT ? "Posizione aggiornata su tutte le mappe" : "Position updated across all maps");
      if (onSaved) onSaved({ lat: pos[0], lng: pos[1] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (IT ? "Errore salvataggio" : "Save error"));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setPos(savedPos);

  // Re-geocode from the property's current address as a shortcut.
  const geocodeFromAddress = async () => {
    if (!property?.address && !property?.city) {
      toast.error(IT ? "Compila prima Città/Indirizzo" : "Fill City/Address first");
      return;
    }
    setGeocoding(true);
    try {
      const q = [property.address, property.city, property.region, "Italy"].filter(Boolean).join(", ");
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`, {
        headers: { "Accept-Language": IT ? "it" : "en" },
      });
      const arr = await r.json();
      if (!arr?.length) {
        toast.error(IT ? "Nessun risultato — sposta il pin manualmente" : "No result — drag the pin manually");
        return;
      }
      const lat = Number(Number(arr[0].lat).toFixed(6));
      const lng = Number(Number(arr[0].lon).toFixed(6));
      setPos([lat, lng]);
      toast.success(IT ? "Ricentrato sull'indirizzo — ora ricontrolla e salva" : "Recentered on address — verify and save");
    } catch {
      toast.error(IT ? "Ricerca fallita" : "Search failed");
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <div className="border-t border-border pt-4 space-y-3" data-testid="position-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <MapPin className="w-4 h-4 text-primary"/>
        <div className="font-medium">
          {IT ? "Posizione sulla mappa" : "Map position"}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {IT ? "Trascina il segnaposto" : "Drag the pin"}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={geocodeFromAddress}
            disabled={geocoding}
            className="lift"
            data-testid="position-geocode-btn"
          >
            <Compass className="w-3.5 h-3.5 mr-1"/>
            {geocoding ? (IT ? "Cerco…" : "Searching…") : (IT ? "Da indirizzo" : "From address")}
          </Button>
          {dirty && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={reset}
              className="text-muted-foreground"
              data-testid="position-reset-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1"/>{IT ? "Annulla" : "Reset"}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 lift"
            data-testid="position-save-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1"/>
            {saving ? (IT ? "Salvo…" : "Saving…") : (IT ? "Salva posizione" : "Save position")}
          </Button>
        </div>
      </div>

      <div
        className="rounded-lg overflow-hidden border border-border relative"
        style={{ height: "340px" }}
        data-testid="position-picker-map"
      >
        <MapContainer
          center={initial}
          zoom={initialZoom}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
          className="hh-vintage-map"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            maxZoom={19}
            subdomains={["a", "b", "c"]}
          />
          <Recenter pos={pos} zoom={16}/>
          <Marker
            position={pos}
            icon={pinIcon}
            draggable
            eventHandlers={eventHandlers}
            ref={markerRef}
          />
        </MapContainer>
      </div>

      <div className="flex items-center justify-between text-[11px] text-muted-foreground flex-wrap gap-2">
        <div>
          <span className="uppercase tracking-widest mr-1.5">Lat</span>
          <span className="font-mono text-foreground" data-testid="position-lat">{pos[0].toFixed(6)}</span>
          <span className="uppercase tracking-widest ml-3 mr-1.5">Lng</span>
          <span className="font-mono text-foreground" data-testid="position-lng">{pos[1].toFixed(6)}</span>
        </div>
        <a
          href={`https://www.openstreetmap.org/?mlat=${pos[0]}&mlon=${pos[1]}#map=17/${pos[0]}/${pos[1]}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
          data-testid="position-osm-link"
        >
          {IT ? "Verifica su OpenStreetMap ↗" : "Verify on OpenStreetMap ↗"}
        </a>
      </div>
    </div>
  );
}
